import React from 'react';
import { buildTransparencySections } from './CalculationTransparency.js';

const calculatorSections = [
  {
    kind: 'accumulation',
    title: 'Accumulation Calculator',
    subtitle: 'For working years, contributions, tax offsets, super growth, Division 296 on high-balance paths, and retirement-balance projections.',
  },
  {
    kind: 'basic',
    title: 'Basic Retirement Calculator',
    subtitle: 'For quick retirement income, Age Pension, fees, and balance projections. Division 296 is out of scope here.',
  },
  {
    kind: 'advanced',
    title: 'Advanced Retirement Calculator',
    subtitle: 'For historical testing, Monte Carlo, phased retirement, alternative assets, lump sums, Division 296, and detailed income modelling.',
  },
  {
    kind: 'adviser',
    title: 'Adviser Session (practice desk)',
    subtitle: 'Advanced engine in a client-meeting layout: household stress paths, Age Pension, Division 296, dual hit rates, and file-ready exports. Educational modelling only.',
  },
];

const smsfSections = [
  'SMSF Setup Transparency',
  'Division 293 Transparency',
  'Division 296 Transparency',
  'Bring-Forward Transparency',
  'FHSS Transparency',
  'LRBA Transparency',
  'Carry-Forward Transparency',
  'Government Co-Contribution Transparency',
  'Downsizer Transparency',
  'SMSF calculator',
].map((title) => ({
  kind: 'smsf',
  title,
  subtitle: title === 'SMSF calculator'
    ? 'General SMSF suite scope for tools that do not have their own dedicated transparency profile yet.'
    : 'Dedicated SMSF tool scope.',
}));

const SectionList = ({ heading, items }) => (
  <div>
    <h4 className="font-semibold text-slate-900 mb-2">{heading}</h4>
    <ul className="list-disc ml-5 space-y-1 text-sm text-slate-700">
      {items.map((item, idx) => (
        <li key={`${heading}-${idx}`}>{item}</li>
      ))}
    </ul>
  </div>
);

const TransparencyCard = ({ section }) => {
  const details = buildTransparencySections(section.kind, { title: section.title });
  const yearlyHeading = section.kind === 'adviser' ? 'How results are shown' : 'Result Trace';
  return (
    <article className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 p-5">
        <h3 className="text-xl font-bold text-slate-950">{section.title}</h3>
        <p className="text-sm text-slate-600 mt-1">{section.subtitle}</p>
      </div>
      <div className="p-5 space-y-5">
        <SectionList heading="Everything This Calculator Can Consider" items={details.inputs} />
        <SectionList heading="Rules Applied" items={details.rules} />
        {details.yearly.length > 0 && (
          <SectionList heading={yearlyHeading} items={details.yearly} />
        )}
      </div>
    </article>
  );
};

const CalculatorTransparencyPage = () => (
  <main className="max-w-6xl mx-auto px-4 py-8">
    <section className="bg-gradient-to-r from-blue-700 to-purple-700 text-white rounded-3xl p-8 shadow-xl mb-8">
      <p className="uppercase tracking-wide text-xs font-bold text-blue-100 mb-2">Calculation transparency</p>
      <h1 className="text-4xl font-black mb-3">What every calculator considers</h1>
      <p className="text-blue-50 max-w-3xl text-lg">
        This page is the plain-English audit list for the calculator suite. It lists the inputs, rules,
        government settings, thresholds, and result traces each calculator can account for.
      </p>
    </section>

    <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8">
      <h2 className="font-bold text-amber-950 mb-2">Important reading note</h2>
      <p className="text-sm text-amber-900">
        These are capability lists. A calculator only applies an item when that input exists, is enabled,
        and is relevant to the selected household or SMSF situation. For example, Rent Assistance is part
        of the Advanced pension modelling for renters with rent entered, while the Basic calculator does
        not normally expose a rent-paid field.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-slate-950 mb-4">Main Retirement Calculators</h2>
      <div className="grid grid-cols-1 gap-6">
        {calculatorSections.filter((s) => s.kind !== 'adviser').map((section) => (
          <TransparencyCard key={section.title} section={section} />
        ))}
      </div>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-slate-950 mb-4">Adviser desk</h2>
      <div className="grid grid-cols-1 gap-6">
        {calculatorSections.filter((s) => s.kind === 'adviser').map((section) => (
          <TransparencyCard key={section.title} section={section} />
        ))}
      </div>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-slate-950 mb-4">SMSF Calculator Suite</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {smsfSections.map((section) => (
          <TransparencyCard key={section.title} section={section} />
        ))}
      </div>
    </section>
  </main>
);

export default CalculatorTransparencyPage;
